import { useLanguage } from '@/context/LanguageContext'
import { DataListOption, EntertainmentData } from '@/services/mobi-api/data/types'
import useIntl from '@/utils/intl'
import { AllInclusive, ExpandMoreOutlined, HelpOutline } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from '@mui/material'
import classNames from 'classnames'
import React, { FunctionComponent } from 'react'

interface Props {
  cardType: number
  item: DataListOption
  clickedOption?: DataListOption
  selectPackage: (item: DataListOption | EntertainmentData) => void
}

const DataCard: FunctionComponent<Props> = ({ cardType, item, clickedOption, selectPackage }) => {
  const { locale } = useLanguage()
  const { translate } = useIntl()

  if (item === undefined) {
    return <div>404</div>
  }

  const dataCartBasic = (): any => (
    <div
      className={classNames(
        'bg-surfaceInverse rounded-2xl flex justify-between py-2 px-3 cursor-pointer border relative',
        clickedOption === item ? 'border-secondary' : 'border-surfaceInverse'
      )}
      onClick={() => selectPackage(item)}
    >
      <div className='absolute bottom-0 right-0 bg-primaryMain rounded-tl-2xl rounded-br-2xl px-2'>
        <span className='text-textAdditional text-xs font-semibold truncate ...'>{item.tagMn}</span>
      </div>
      <div className='flex flex-col'>
        <span className='text-sm text-textMid font-medium'>
          {item.duration} {item.duration > 1 ? translate('days') : translate('day')}
        </span>
        <span className='text-textHigh font-semibold text-lg'>
          {item.volume}
        </span>
        <span className='text-textHigh text-xs mt-1'>{item.price}₮</span>
      </div>
      <div className='flex flex-col justify-between'>
        <Tooltip title={locale === 'en' ? item.noteEn : item.noteMn} enterTouchDelay={0}>
          <HelpOutline className='text-textHigh' />
        </Tooltip>
      </div>
    </div>
  )

  const dataCardSpecial = (): any => (
    <div
      className={classNames('p-3 cursor-pointer border bg-surfaceInverse rounded-2xl', clickedOption === item ? 'border-secondary' : 'border-surfaceInverse')}
      onClick={() => selectPackage(item)}
    >
      <div className='flex justify-between items-end'>
        <div className='flex items-end gap-3'>
          <div className='flex flex-col'>
            <span className='text-sm text-textMid font-medium'>
              {item.duration} {item.duration > 1 ? translate('days') : translate('day')}
            </span>
            {
              item.volume === 'unlimited'
                ? (
                  <div className='flex gap-3 items-end'>
                    <span className='text-textHigh font-semibold text-lg'>
                      {(locale === 'en' ? item.nameEn : item.nameMn)}
                    </span>
                    <span className='text-primaryMain text-sm font-bold'><AllInclusive /> {translate('datakey')}</span>
                  </div>
                  )
                : (
                  <span className='text-textHigh font-semibold'>
                    {(locale === 'en' ? item.nameEn : item.nameMn)}
                  </span>
                  )
            }
          </div>
        </div>
        <div className='flex justify-center items-center'>
          <span className='text-textHigh text-sm font-bold mr-2'>{item.price}₮</span>
        </div>
      </div>
      {
          clickedOption === item &&
          (
            <ul className='mt-5 md:w-80'>
              {(locale === 'en' ? item.descriptionEn : item.descriptionMn)
                .split('\n')
                .map((line, index) => (
                  <li key={index} className='text-xs text-textMid'>• {line}</li>
                ))}
            </ul>

          )
        }
    </div>
  )

  const dataCardEntertainment = (): any => (
    <Accordion className='rounded-2xl bg-surfaceInverse' elevation={0} disableGutters sx={{ '&:before': { height: '0px' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreOutlined fontSize='small' className='text-textHigh' />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <div className='flex items-center gap-3'>
          {
            item.subGroupIcon !== undefined && item.subGroupIcon !== ''
              ? <img src={item.subGroupIcon} />
              : <></>
          }
          <p className='font-bold text-sm text-textHigh whitespace-nowrap'>{locale === 'en' ? item.subGroupEn : item.subGroupMn}</p>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className='grid grid-cols-2 gap-4'>
          {
            item.datas?.map((data, idx) => (
              <div
                key={idx} className={classNames('flex flex-col border p-2 rounded-xl cursor-pointer', clickedOption === data ? 'border-secondary' : 'border-secondarySoftLow')}
                onClick={() => selectPackage(data)}
              >
                {
                  data.img !== undefined && data.img !== ''
                    ? <img src={data.img} className='aspect-square rounded-full w-6 object-contain' />
                    : <></>

                }
                <span className='text-textMid text-xs mt-2'>{data.name}</span>
                <div className='flex justify-between items-center'>
                  <span className='font-bold text-textHigh'>{data.volume}</span>
                  <span className='text-textMid text-sm'>{data.price}₮</span>

                </div>
              </div>
            ))
          }
        </div>
      </AccordionDetails>
    </Accordion>
  )

  return (
    cardType === 2
      ? (
          dataCardSpecial()
        )
      : cardType === 3
        ? (
            dataCardEntertainment()
          )
        : (
            dataCartBasic()
          )
  )
}

export default DataCard
